<template>
  <div class="add-pet-page" :style="{ backgroundImage: `url(${designUrl}AddPetPage.jpeg)` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('UsersFavouritesPage')">My Favourites</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">Add Your Pet</h1>
      <p class="page-sub">Fill in your pet's details to create a profile</p>
    </div>

    <div class="page-wrapper">
      <div class="form-card">

        <div class="form-group">
          <label class="form-label">Pet's Name</label>
          <input v-model="pet.pet_name" type="text" class="form-input" placeholder="e.g. Buddy" required />
        </div>

        <div class="form-group">
          <label class="form-label">Breed</label>
          <select v-model="selectedBreed" class="form-input form-select">
            <option disabled value="">Select a breed</option>
            <option v-for="breed in breeds" :key="breed.id" :value="breed.name">{{ breed.name }}</option>
            <option value="custom">Add my pet's breed</option>
          </select>
        </div>

        <div class="form-group" v-if="selectedBreed === 'custom'">
          <label class="form-label">New Breed Name</label>
          <div class="inline-row">
            <input type="text" v-model="newBreed" placeholder="Enter breed name" class="form-input" />
            <button @click="addNewBreed" :disabled="!newBreed" class="inline-btn" type="button">Add</button>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Age</label>
          <div class="pill-group">
            <label
              v-for="option in ['Puppy', 'Adult', 'Senior']"
              :key="option"
              class="pill-option"
              :class="{ 'pill-active': pet.age === option }"
            >
              <input type="radio" v-model="pet.age" :value="option" hidden />
              {{ option }}
            </label>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Gender</label>
          <div class="pill-group">
            <label class="pill-option pill-male" :class="{ 'pill-active': pet.gender === 'Male' }">
              <input type="radio" v-model="pet.gender" value="Male" hidden />
              Male
            </label>
            <label class="pill-option pill-female" :class="{ 'pill-active': pet.gender === 'Female' }">
              <input type="radio" v-model="pet.gender" value="Female" hidden />
              Female
            </label>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">
            Description
            <button type="button" @click="generateDescription" class="ai-btn">✨ Generate with AI</button>
          </label>
          <textarea v-model="pet.description" class="form-textarea" placeholder="Describe your pet..."></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Pet Photo</label>
          <input ref="fileInput" type="file" accept="image/*" @change="handleFileUpload" class="file-input" />
        </div>

        <div class="form-group checkbox-row">
          <input type="checkbox" v-model="pet.isOnline" class="checkbox-input" id="visibility" />
          <label class="checkbox-label" for="visibility">Make Pet Profile Visible</label>
        </div>

        <button
          class="submit-btn"
          type="button"
          :disabled="isLoading"
          @click="addPet"
        >
          <span v-if="isLoading">Adding...</span>
          <span v-else>Add Pet</span>
        </button>

        <div v-if="successMessage" class="feedback success">{{ successMessage }}</div>
        <div v-if="errorMessage" class="feedback error">{{ errorMessage }}</div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const backendUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    user: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      designUrl: '/background_designs/',
      pet: {
        pet_name: '',
        breed: '',
        other_breed: '',
        age: '',
        gender: '',
        description: '',
        photo: null,
        isOnline: true,
        location: ''
      },
      isLoading: false,
      isAuthenticated: false,
      breeds: [],
      selectedBreed: '',
      newBreed: '',
      sidebarOpen: false,
      successMessage: '',
      errorMessage: '',
      petsOpen: false,
    };
  },
  created() {
    this.fetchBreeds();
  },
  methods: {
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    async fetchBreeds() {
      const res = await axios.get(`${backendUrl}/api/breeds/breeds`, { withCredentials: true });
      this.breeds = res.data;
    },

    async addNewBreed() {
      if (!this.newBreed) return;
      try {
        const res = await axios.post(`${backendUrl}/api/breeds/breeds`, { name: this.newBreed }, { withCredentials: true });
        this.breeds.push(res.data);
        this.selectedBreed = this.newBreed;
        this.newBreed = '';
      } catch (err) {
        console.log('Could not add breed: ' + err.response?.data?.error || err.message);
      }
    },

    async generateDescription() {
      const name = this.pet.pet_name;
      const age = this.getAgeBracket(this.pet.age);
      const gender = this.pet.gender;
      const selected = this.selectedBreed;
      const custom = this.newBreed.trim();
      const breed = selected === 'custom' ? custom : selected;

      if (!name || !breed || !age || !gender) {
        console.log('Please fill in name, breed, age, and gender first.');
        return;
      }

      try {
        const response = await axios.post(
          `${backendUrl}/api/pets/generate-description`,
          {
            pet_name: name,
            selectedBreed: selected,
            newBreed: custom,
            age: age,
            gender: gender
          },
          { withCredentials: true }
        );
        this.pet.description = response.data.description;
      } catch (error) {
        console.error('Error generating description:', error);
      }
    },

    getAgeBracket(age) {
      if (age === 'Puppy') return 'Puppy';
      if (age === 'Adult') return 'Adult';
      if (age === 'Senior') return 'Senior';
      return '';
    },

    async addPet() {
      this.successMessage = '';
      this.errorMessage = '';

      const finalBreed = this.selectedBreed === 'custom'
        ? this.newBreed.trim()
        : this.selectedBreed;

      if (!finalBreed) {
        this.errorMessage = 'Please pick or enter a breed.';
        return;
      }

      this.pet.breed = finalBreed;

      if (!this.pet.pet_name || !finalBreed || !this.pet.age || !this.pet.gender || !this.pet.description || !this.pet.photo) {
        this.errorMessage = 'Please fill in all the details.';
        return;
      }

      if (this.pet.photo && !this.pet.photo.type.startsWith('image/')) {
        this.errorMessage = 'Please upload a valid image file.';
        return;
      }

      try {
        this.isLoading = true;

        const formData = new FormData();
        formData.append('pet_name', this.pet.pet_name);
        formData.append('breed', finalBreed);
        formData.append('custom_breed', this.newBreed.trim());
        formData.append('age', this.pet.age);
        formData.append('gender', this.pet.gender);
        formData.append('description', this.pet.description);
        formData.append('photo', this.pet.photo);
        formData.append('isOnline', this.pet.isOnline);

        const response = await axios.post(
          `${backendUrl}/api/pets/add-pet`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true
          }
        );

        console.log('✅ Pet added:', response.data);
        this.successMessage = 'Pet added successfully!';
        this.resetForm();

      } catch (err) {
        console.error('❌ Error adding pet:', err);
        this.errorMessage = 'Error adding pet. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    resetForm() {
      this.pet = {
        pet_name: '',
        breed: '',
        age: '',
        gender: '',
        description: '',
        photo: null,
        isOnline: true
      };
      this.selectedBreed = '';
      this.newBreed = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      this.pet.photo = file;
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.add-pet-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* ── Page header ── */
.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 70px 20px 0;
  text-align: left;
}

.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
}

.page-sub {
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
  margin-top: 4px;
}

/* ── Page wrapper ── */
.page-wrapper {
  max-width: 520px;
  margin: 0 auto;
  padding: 24px 20px 60px;
}

/* ── Form card ── */
.form-card {
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid #f0e0ff;
  padding: 28px 24px;
  box-shadow: 0 4px 24px rgba(186, 113, 255, 0.08);
}

/* ── Form groups ── */
.form-group {
  margin-bottom: 22px;
}

.form-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  font-size: 15px;
  background: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
  font-family: inherit;
}

.form-input:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23aaa' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  cursor: pointer;
}

/* Inline row for custom breed */
.inline-row {
  display: flex;
  gap: 10px;
}

.inline-row .form-input { flex: 1; }

.inline-btn {
  padding: 0 20px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s;
  white-space: nowrap;
}

.inline-btn:hover { background: #ba71ff; color: #fff; }
.inline-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Pill selectors ── */
.pill-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.pill-option {
  flex: 1;
  text-align: center;
  padding: 8px 18px;
  border-radius: 999px;
  border: 1.5px solid #f0e0ff;
  background: #fffbe8;
  color: #888;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  user-select: none;
}

.pill-option:hover {
  border-color: #ba71ff;
  color: #4a1070;
}

.pill-option.pill-active {
  background: #eac7ff;
  border-color: #ba71ff;
  color: #4a1070;
}

.pill-male.pill-active  { background: #eff6ff; border-color: #2563eb; color: #1d4ed8; }
.pill-female.pill-active { background: #fdf2f8; border-color: #db2777; color: #be185d; }

/* ── Textarea ── */
.form-textarea {
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  font-size: 15px;
  background: #fffbe8;
  outline: none;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  color: #1a1a1a;
  transition: all 0.2s ease;
}

.form-textarea:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

/* ── AI button ── */
.ai-btn {
  padding: 4px 12px;
  background: #fffbe8;
  border: 1px solid #f6d860;
  border-radius: 999px;
  color: #92610a;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.ai-btn:hover { background: #fff3b0; }

/* ── File input ── */
.file-input {
  width: 100%;
  padding: 11px 14px;
  border: 1.5px dashed #f0e0ff;
  border-radius: 14px;
  font-size: 14px;
  background: #fffbe8;
  cursor: pointer;
  color: #888;
}

/* ── Checkbox ── */
.checkbox-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  accent-color: #ba71ff;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-label {
  font-size: 14px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
}

/* ── Submit button ── */
.submit-btn {
  width: 100%;
  padding: 14px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 6px;
}

.submit-btn:hover { background: #ba71ff; color: #fff; }
.submit-btn:active { transform: scale(0.98); }
.submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Feedback ── */
.feedback {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.success { background: #d4f5e2; color: #1a6b3c; }
.error   { background: #fde8e8; color: #991b1b; }

/* ── Responsive ── */
@media (max-width: 480px) {
  .page-wrapper { padding: 20px 14px 40px; }
  .form-card { padding: 20px 16px; }
  .pill-option { font-size: 12px; padding: 7px 14px; }
}
</style>